import { useState, useContext, useRef, CSSProperties, Suspense } from 'react'
import styled from '@emotion/styled'
import { MultiGrid } from 'react-virtualized'
import { Status } from '@global/hooks/useClassificationRowStatus'
import { lazyImport } from '@global/utils/lazyImport'
import { cn } from '@global/utils/tailwind'
import * as css from './styles'
import { Column, ErrorsByType, Row } from './types'
import {
  isFirstSelectedRow,
  getInvalidListValues,
  getRowType,
  getErrorClass,
  getCellData,
  getSplitCellValue,
  getShowChipsTooltip,
  getDataTip,
} from './utils'
import { SELECTION_LIMIT_MSG } from '../../ReadGridConstants'
import { ReadGridColumnWidthContext } from '../../context/column-width-context'
import { ReadGridContextMenuContext } from '../../context/context-menu-context'
import { LinkStatus } from '../../link-status-renderer'
import { DisabledPopover } from '../DisabledPopover'
import { ErrorPopover } from '../ReadGridErrorPopover'
import { RejectionCommentPopover } from '../RejectionCommentPopover'
import { CommentPopover } from '../commentPopover'
import { groupErrorsByType } from '../errors/ErrorsUtil'
import { RowPropertyValue } from '../rowPropertyValue'
import { t } from '@lingui/core/macro'

const { ImagePreviewHandler } = lazyImport(
  () => import(/* webpackChunkName: "imagePreviewHandler" */ '../image-preview-handler'),
)
const { AiClassificationsOverlayHandler } = lazyImport(
  /* webpackChunkName: "aiClassificationsOverlayHandler" */ () =>
    import('../ai-classifications-overlay-handler'),
)
const { NestedValusRenderer } = lazyImport(
  /* webpackChunkName: "nestedValusRenderer" */ () => import('../nested-cell-value-renderer'),
)

const Header = styled.div`
  padding-bottom: 4px;
  font-weight: bold;
`

interface Props {
  column: Column
  columnIndex: number
  columns: Column[]
  parent: MultiGrid
  row: Row
  rowIndex: number
  selectedCells: Record<string, boolean>
  style: CSSProperties
  isCheckboxDisabled: boolean
  creativeClassicationsEnabled?: boolean
  chipsListEnabled?: boolean
  imagePreviewEnabled?: boolean
}

export function ReadGridDataCell({
  column,
  columnIndex,
  columns,
  parent,
  row,
  rowIndex,
  selectedCells,
  style,
  isCheckboxDisabled,
  creativeClassicationsEnabled = false,
  chipsListEnabled = false,
  imagePreviewEnabled = false,
}: Props) {
  const { selected, selectedRows } = parent.props
  const { fieldId } = column
  const rowPropertyName = column?.rowPropertyName
  const isRowProperty = Boolean(rowPropertyName)
  const cvId = row?.cvid || ''
  const isDisabled = Boolean(row?.isDisabled)
  const isRemoving = Boolean(row?.isRemoving)

  const dataDiv = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const { rowMode } = useContext(ReadGridColumnWidthContext)
  const { openContextMenu } = useContext(ReadGridContextMenuContext)

  if (!row) {
    return <css.CellPlaceholder>—</css.CellPlaceholder>
  }

  const cellData = getCellData({
    row,
    fieldId,
    property: rowPropertyName,
    properties: row?.properties,
    governanceType: column?.governanceType,
    isClassificationColumn: column?.isClassificationColumn,
  })

  const isSelected = selected[`${rowIndex}_${columnIndex}`] || selectedRows[rowIndex]
  const open = Boolean(anchorEl)
  const disabledPopoverOpen = open && (isDisabled || isRemoving)
  const checkboxDisabled = open && isCheckboxDisabled
  const rejectionComments = cellData?.comments ?? {}
  const hasCommentApproverView = Boolean(cellData?.comment)
  const hasCommentUserView = Boolean(cellData?.comments)
  const cellHasComment = hasCommentApproverView || hasCommentUserView

  const errorsByType: ErrorsByType = groupErrorsByType(cellData)
  const rowType = getRowType(rowIndex, isRowProperty)
  const formattedData = getFormattedData({
    cellData,
    column,
  })
  const isFirstSelected = isFirstSelectedRow({ selectedCells, rowIndex, columnIndex })
  const errorClass = getErrorClass(errorsByType, cellHasComment)
  const cellClassNames = cn({
    'read-grid-cell': true,
    'read-grid-cell-data': true,
    'first-selected': isFirstSelected,
    disabled: isDisabled || isCheckboxDisabled,
    selected: isSelected,
    [errorClass]: !isDisabled,
  })
  const splitCellValue = getSplitCellValue(chipsListEnabled, cellData, column?.listDelimiter)
  const showChipsTooltip = getShowChipsTooltip(chipsListEnabled, column)
  const dataTip = getDataTip(showChipsTooltip, splitCellValue, cellData)

  return (
    <css.Cell
      ref={dataDiv}
      data-column={columnIndex}
      data-row={rowIndex}
      data-nth-row-type={rowType}
      data-tip={dataTip}
      data-html={true}
      className={cellClassNames}
      style={style}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      onContextMenu={handleContextMenu}
    >
      <div css={css?.[rowMode.toLowerCase()]}>
        {isRowProperty ? (
          <RowPropertyValue cellData={cellData} anchorEl={anchorEl} open={open} />
        ) : (
          formattedData
        )}
      </div>

      <ErrorPopover
        errorsByType={errorsByType}
        open={open && !isDisabled}
        anchorEl={anchorEl}
        handlePopoverClose={handlePopoverClose}
        value={cellData?.value}
        fieldId={fieldId}
      />

      {hasCommentUserView && (
        <RejectionCommentPopover
          anchorEl={anchorEl}
          open={open}
          rejectionComments={rejectionComments}
          handlePopoverClose={handlePopoverClose}
        />
      )}

      {hasCommentApproverView && (
        <CommentPopover
          anchorEl={anchorEl}
          cvId={cvId}
          fieldId={fieldId}
          open={open}
          rowIdx={rowIndex - 1}
          handlePopoverClose={handlePopoverClose}
        />
      )}

      {checkboxDisabled && (
        <DisabledPopover
          anchorEl={anchorEl}
          open={checkboxDisabled}
          handlePopoverClose={handlePopoverClose}
        >
          <div>{SELECTION_LIMIT_MSG}</div>
        </DisabledPopover>
      )}

      <DisabledPopover
        anchorEl={anchorEl}
        open={disabledPopoverOpen}
        handlePopoverClose={handlePopoverClose}
      >
        <>
          <Header>{t`Processing row...`}</Header>
          <div>
            {isRemoving
              ? 'This row is being removed'
              : 'This row is being processed as a part of another submission'}
          </div>
        </>
      </DisabledPopover>
    </css.Cell>
  )

  function handlePopoverClose() {
    setAnchorEl(null)
  }

  function handleContextMenu(e) {
    openContextMenu({
      event: e,
      grid: parent,
      cellData,
      rowData: row,
      column,
      cellRef: dataDiv,
      selectedCells,
      columns,
    })
  }

  function handlePopoverOpen(e) {
    setAnchorEl(e.currentTarget)
  }

  function getFormattedData({
    cellData,
    column,
  }: {
    cellData?: { value?: string } | LinkStatusCellData
    column: Column
  }) {
    const cellValue = cellData?.value

    switch (column.governanceType) {
      case 'date':
        return cellValue
      case 'deep-link':
      case 'link':
      case 'qr-code':
        return (
          <span style={{ padding: '5px' }}>
            <a href={cellValue} target="_blank" rel="noreferrer">
              {cellValue}
            </a>
          </span>
        )
      case 'link-status':
        if (isLinkStatusCellData(cellData)) {
          return (
            <LinkStatus
              urlAudits={cellData.urlAudits}
              linkGovernance={cellData.linkGovernance}
              linkValue={cellData.linkValue}
              rowNum={cellData.rowNum}
            />
          )
        }
        break
      case 'list': {
        if (!cellValue) return
        const delimiter = column.listDelimiter

        const values = delimiter ? cellValue.split(delimiter) : [cellValue]

        const invalidListValues = getInvalidListValues({ cellData })

        return values.map((val, index) => {
          const listClasses = cn({
            '-invalid-value': invalidListValues.includes(val),
          })

          const delimiterExcludedOnLastItem = values.length - 1 !== index ? delimiter : null
          return (
            <span
              key={`${val}-${String(index)}`}
              className={[listClasses, 'read-grid-list-value'].join(' ')}
            >
              {val}
              {delimiterExcludedOnLastItem}
            </span>
          )
        })
      }
      default:
        if (!cellValue) return

        return (
          <DefaultDataCell
            cellValue={cellValue}
            classificationColumns={columns.filter(column => column.isClassificationColumn)}
            creativeClassicationsEnabled={creativeClassicationsEnabled}
            imagePreviewEnabled={imagePreviewEnabled}
            column={column}
            row={row}
          />
        )
    }
  }
}

const isLinkStatusCellData = (cellData?: object): cellData is LinkStatusCellData =>
  !!cellData && 'urlAudits' in cellData && 'linkGovernance' in cellData && 'linkValue' in cellData

interface LinkStatusCellData {
  urlAudits: unknown
  linkGovernance: unknown
  linkValue: unknown
  rowNum: number
  value?: string
}

interface DefaultDataCellProps {
  cellValue: any
  creativeClassicationsEnabled: boolean
  imagePreviewEnabled: boolean
  column: Column
  classificationColumns: Column[]
  row: Row
}

function DefaultDataCell({
  cellValue,
  creativeClassicationsEnabled,
  imagePreviewEnabled,
  column,
  row,
  classificationColumns,
}: DefaultDataCellProps) {
  if (creativeClassicationsEnabled && column.isSourceUrl) {
    return (
      <Suspense fallback={<div>{t`Loading...`}</div>}>
        <AiClassificationsOverlayHandler
          classificationColumns={classificationColumns}
          categories={row.classification?.summary?.categories || []}
          classificationRowStatus={row?.classificationRowStatus || Status.UNCLASSIFIED}
        >
          {imagePreviewEnabled && column.isPreviewUrl ? (
            <ImagePreviewHandler imageUrl={cellValue} cvId={row.cvid}>
              <css.ImagePreviewIndicator>{cellValue}</css.ImagePreviewIndicator>
            </ImagePreviewHandler>
          ) : (
            <span style={{ lineHeight: '1.8' }}>{cellValue}</span>
          )}
        </AiClassificationsOverlayHandler>
      </Suspense>
    )
  }

  if (imagePreviewEnabled && column.isPreviewUrl && !column.isSourceUrl) {
    return (
      <Suspense fallback={<div>{t`Loading...`}</div>}>
        <ImagePreviewHandler imageUrl={cellValue} cvId={row.cvid}>
          <css.ImagePreviewIndicator>{cellValue}</css.ImagePreviewIndicator>
        </ImagePreviewHandler>
      </Suspense>
    )
  }

  if (creativeClassicationsEnabled && column.isClassificationColumn && cellValue.hasNestedValues) {
    return (
      <Suspense fallback={<div>{t`Loading...`}</div>}>
        <NestedValusRenderer cellValue={cellValue} />
      </Suspense>
    )
  }

  return cellValue
}
